import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import ru from './locales/ru.json';
import en from './locales/en.json';
import ka from './locales/ka.json';
import hy from './locales/hy.json';
import az from './locales/az.json';
import it from './locales/it.json';
import fr from './locales/fr.json';

export const SUPPORTED_LANGUAGES = [
  'ru',
  'en',
  'ka',
  'hy',
  'az',
  'it',
  'fr'
];

const MANUAL_LANGUAGE_KEY = 'bgq_language';
const AUTO_LANGUAGE_KEY = 'bgq_auto_language';
const COUNTRY_CODE_KEY = 'bgq_country_code';

const COUNTRY_LANGUAGE_MAP = {
  AM: 'hy',
  GE: 'ka',
  AZ: 'az',
  IT: 'it',
  FR: 'fr',

  RU: 'ru',
  BY: 'ru',
  KZ: 'ru',
  KG: 'ru',
  TJ: 'ru',
  TM: 'ru',
  UZ: 'ru',
  MD: 'ru'
};

function normalizeLanguage(language) {
  const code = String(language || '')
    .trim()
    .toLowerCase()
    .split('-')[0];

  return SUPPORTED_LANGUAGES.includes(code)
    ? code
    : null;
}

function normalizeCountryCode(countryCode) {
  const code = String(countryCode || '')
    .trim()
    .toUpperCase();

  return /^[A-Z]{2}$/.test(code)
    ? code
    : null;
}

function safeGetStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(
      `[i18n] Не удалось прочитать ${key}:`,
      error
    );

    return null;
  }
}

function safeSetStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn(
      `[i18n] Не удалось сохранить ${key}:`,
      error
    );
  }
}

function safeRemoveStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(
      `[i18n] Не удалось удалить ${key}:`,
      error
    );
  }
}

function getManualLanguage() {
  return normalizeLanguage(
    safeGetStorage(MANUAL_LANGUAGE_KEY)
  );
}

function getCachedAutoLanguage() {
  return normalizeLanguage(
    safeGetStorage(AUTO_LANGUAGE_KEY)
  );
}

function getBrowserLanguage() {
  try {
    const browserLanguages = Array.isArray(
      navigator.languages
    )
      ? navigator.languages
      : [navigator.language];

    for (const browserLanguage of browserLanguages) {
      const normalized =
        normalizeLanguage(browserLanguage);

      if (normalized) {
        return normalized;
      }
    }
  } catch (error) {
    console.warn(
      '[i18n] Не удалось определить язык браузера:',
      error
    );
  }

  return null;
}

function getInitialLanguage() {
  return (
    getManualLanguage() ||
    getCachedAutoLanguage() ||
    getBrowserLanguage() ||
    'ru'
  );
}

function languageFromCountry(countryCode) {
  const normalizedCountry =
    normalizeCountryCode(countryCode);

  if (!normalizedCountry) {
    return null;
  }

  return (
    COUNTRY_LANGUAGE_MAP[normalizedCountry] ||
    'en'
  );
}


async function fetchCountryCode() {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, 3500);

  try {
    const response = await fetch(
      'https://ipapi.co/country/',
      {
        method: 'GET',
        headers: {
          Accept: 'text/plain'
        },
        signal: controller.signal,
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        `Geo HTTP ${response.status}`
      );
    }

    const countryCode = normalizeCountryCode(
      await response.text()
    );

    if (!countryCode) {
      throw new Error(
        'Geo-сервис вернул некорректный код страны'
      );
    }

    return countryCode;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function initializeLanguageFromCountry() {
  const manualLanguage = getManualLanguage();

  // Страна должна соответствовать текущему IP.
  // Удаляем старый код до нового запроса, чтобы Lobby
  // не успел отправить на сервер страну от прошлого VPN.
  safeRemoveStorage(COUNTRY_CODE_KEY);

  try {
    const countryCode = await fetchCountryCode();
    const automaticLanguage =
      languageFromCountry(countryCode);

    safeSetStorage(COUNTRY_CODE_KEY, countryCode);

    if (!automaticLanguage) {
      return;
    }

    safeSetStorage(
      AUTO_LANGUAGE_KEY,
      automaticLanguage
    );

    // Ручной язык всегда главнее IP.
    if (manualLanguage || getManualLanguage()) {
      return;
    }

    if (
      normalizeLanguage(i18n.language) !==
      automaticLanguage
    ) {
      await i18n.changeLanguage(
        automaticLanguage
      );
    }
  } catch (error) {
    console.warn(
      '[i18n] Автоопределение языка по стране недоступно:',
      error
    );
  }
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      ru: {
        translation: ru
      },

      en: {
        translation: en
      },

      ka: {
        translation: ka
      },

      hy: {
        translation: hy
      },

      az: {
        translation: az
      },

      it: {
        translation: it
      },

      fr: {
        translation: fr
      }
    },

    lng: getInitialLanguage(),
    fallbackLng: 'ru',

    supportedLngs: SUPPORTED_LANGUAGES,

    load: 'languageOnly',

    interpolation: {
      escapeValue: false
    },

    returnNull: false,
    returnEmptyString: false
  });

export async function setLanguageManually(language) {
  const normalizedLanguage =
    normalizeLanguage(language);

  if (!normalizedLanguage) {
    throw new Error(
      `Неподдерживаемый язык: ${language}`
    );
  }

  safeSetStorage(
    MANUAL_LANGUAGE_KEY,
    normalizedLanguage
  );

  await i18n.changeLanguage(
    normalizedLanguage
  );
}

export function getDetectedCountryCode() {
  return normalizeCountryCode(
    safeGetStorage(COUNTRY_CODE_KEY)
  );
}

i18n.on('languageChanged', (language) => {
  const normalizedLanguage =
    normalizeLanguage(language) || 'ru';

  if (typeof document !== 'undefined') {
    document.documentElement.lang =
      normalizedLanguage;
  }
});

if (typeof document !== 'undefined') {
  document.documentElement.lang =
    normalizeLanguage(
      i18n.resolvedLanguage ||
      i18n.language ||
      'ru'
    ) || 'ru';
}

void initializeLanguageFromCountry();

export default i18n;
